import { useEffect, useMemo } from "react";
import { fetchCleaners } from "../../../store/slices/cleanerSlice";
import {
  fetchCleaningTasks,
  updateCleaningTask,
} from "../../../store/slices/cleaningTaskSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { CleaningTaskResponse } from "../../../models/ResponseModels/CleaningTaskResponse";
import { canManageTeamMembers } from "../../../utils/helperFunctions/roleHelper";

export const useCleaningTasksScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { cleaningTasks, loading, error } = useAppSelector(
    (state) => state.cleaningTasks,
  );

  const { cleaners, loading: cleanersLoading } = useAppSelector(
    (state) => state.cleaners,
  );

  const { user } = useAppSelector((state) => state.auth);

  const canAssignTasks = canManageTeamMembers(user?.role);

  useEffect(() => {
    dispatch(fetchCleaningTasks());

    if (canAssignTasks) {
      dispatch(fetchCleaners());
    }
  }, [canAssignTasks, dispatch]);

  const todayTasks = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    return cleaningTasks
      .filter((task) => task.scheduled_date === today && task.status !== "done")
      .sort((first, second) =>
        String(first.scheduled_time || "").localeCompare(
          String(second.scheduled_time || ""),
        ),
      );
  }, [cleaningTasks]);

  const upcomingTasks = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    return cleaningTasks
      .filter((task) => task.scheduled_date > today && task.status !== "done")
      .sort((first, second) =>
        first.scheduled_date.localeCompare(second.scheduled_date),
      );
  }, [cleaningTasks]);

  const doneTasks = useMemo(() => {
    return cleaningTasks
      .filter((task) => task.status === "done")
      .sort((first, second) => {
        const firstDate = new Date(
          first.completed_at || first.updated_at || first.scheduled_date,
        ).getTime();
        const secondDate = new Date(
          second.completed_at || second.updated_at || second.scheduled_date,
        ).getTime();

        return secondDate - firstDate;
      });
  }, [cleaningTasks]);

  const assignableUsers = useMemo(() => {
    return cleaners.filter((cleaner) => cleaner.role === "cleaner");
  }, [cleaners]);

  const refreshTasks = () => {
    dispatch(fetchCleaningTasks());

    if (canAssignTasks) {
      dispatch(fetchCleaners());
    }
  };

  const assignTask = (
    task: CleaningTaskResponse,
    assignedToUserId: number | null,
  ) => {
    dispatch(
      updateCleaningTask({
        id: task.id,
        body: { assigned_to_user_id: assignedToUserId },
      }),
    );
  };

  const markInProgress = (task: CleaningTaskResponse) => {
    dispatch(
      updateCleaningTask({
        id: task.id,
        body: { status: "in_progress" },
      }),
    );
  };

  const markDone = (task: CleaningTaskResponse) => {
    const today = new Date().toISOString().slice(0, 10);

    if (task.scheduled_date > today) {
      window.alert(t("cleaning.futureTaskCannotBeCompleted"));
      return;
    }

    dispatch(
      updateCleaningTask({
        id: task.id,
        body: { status: "done" },
      }),
    );
  };

  return {
    t,
    user,
    loading: loading || cleanersLoading,
    error,
    todayTasks,
    upcomingTasks,
    doneTasks,
    assignableUsers,
    canAssignTasks,
    refreshTasks,
    assignTask,
    markInProgress,
    markDone,
  };
};
